import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const api = "https://appapi.unpaidfinancial.com/api";

const initials = (u) =>
  ((`${u?.prenom ?? ""} ${u?.nom ?? ""}`.trim() || u?.name || "?")
    .split(" ").map(s=>s.charAt(0)).join("").substring(0,2).toUpperCase());

// --- Helpers IBAN UI ---
const formatIban = (iban) => iban ? iban.replace(/\s+/g,'').replace(/(.{4})/g,'$1 ').trim() : null;
const maskIban = (iban) => {
  if (!iban) return null;
  const s = iban.replace(/\s+/g,'');
  const len = s.length;
  const left = len > 10 ? 6 : Math.max(0, Math.floor(len/4));
  const right = len > 10 ? 4 : Math.max(0, Math.floor(len/4));
  const mid = Math.max(0, len - left - right);
  return s.slice(0,left) + '•'.repeat(mid) + s.slice(len-right);
};

const VirtualCard = ({ card, holder }) => (
  <div className="vf-card">
    <div className="vf-chip" />
    <div className="vf-brand">{card?.brand || "VIRTUAL"}</div>
    <div className="vf-number">{card?.number_masked || "•••• •••• •••• ••••"}</div>
    <div className="d-flex justify-content-between mt-2">
      <div className="vf-label">
        Titulaire
        <div className="vf-value">{holder || "Titulaire"}</div>
      </div>
      <div className="vf-label text-end">
        Expire
        <div className="vf-value">{card ? `${card.exp_month}/${card.exp_year}` : "MM/AAAA"}</div>
      </div>
    </div>
  </div>
);

export default function ValidatedList() {
  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [demandes, setDemandes] = useState([]);        // demandes validées
  const [accounts, setAccounts] = useState({});        // map userId -> {account, card}
  const [loading, setLoading] = useState(true);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [error, setError] = useState("");

  // Create PIN modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createTarget, setCreateTarget] = useState(null); // {id, prenom, nom, email}
  const [createPin, setCreatePin] = useState("");
  const [createDevise, setCreateDevise] = useState("EUR");
  const [creating, setCreating] = useState(false);

  // Update PIN modal
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateTarget, setUpdateTarget] = useState(null); // {id, prenom, nom, email}
  const [updatePin, setUpdatePin] = useState("");
  const [updating, setUpdating] = useState(false);

  // Charger les demandes validées
  useEffect(() => {
    (async () => {
      try {
        if (!token) {
          setError("Utilisateur non authentifié");
          setLoading(false);
          return;
        }
        const { data } = await axios.get(`${api}/demandes`, auth);
        const filtered = (data || []).filter((d) =>
          /(valid|accept)/i.test(String(d.statut || ""))
        );
        setDemandes(filtered);
      } catch (e) {
        setError("Erreur lors de la récupération des demandes");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]); // eslint-disable-line

  // Pour chaque demande validée, chercher s’il existe déjà un compte
  useEffect(() => {
    const loadAccounts = async () => {
      if (!demandes.length) return;
      setLoadingLookups(true);
      try {
        const uniqueEmails = Array.from(
          new Set(
            demandes.map(d => d.email || d.user?.email).filter(Boolean)
          )
        );

        // lookup en parallèle
        const results = await Promise.allSettled(
          uniqueEmails.map(email =>
            axios.get(`${api}/bank/accounts/lookup`, { ...auth, params: { email } })
          )
        );

        const byEmail = {};
        results.forEach((r, idx) => {
          const email = uniqueEmails[idx];
          if (r.status === "fulfilled" && r.value?.data?.user?.id) {
            byEmail[email] = r.value.data; // { user, account }
          }
        });

        // construire map userId -> account
        const map = {};
        demandes.forEach(d => {
          const email = d.email || d.user?.email;
          const info = byEmail[email];
          if (info?.user?.id) {
            map[info.user.id] = info.account || null;
          }
        });

        setAccounts(map);
      } finally {
        setLoadingLookups(false);
      }
    };
    loadAccounts();
  }, [demandes]); // eslint-disable-line

  const holderName = (d) =>
    (`${d.prenom ?? ""} ${d.nom ?? ""}`.trim()) || d.user?.name || "Utilisateur";

  const userIdOf = (d) => d.user_id || d.id || d.user?.id; // robustesse selon payload

  const openCreate = (d) => {
    setCreateTarget({ id: userIdOf(d), prenom: d.prenom, nom: d.nom, email: d.email || d.user?.email, name: d.user?.name });
    setCreatePin("");
    setCreateDevise("EUR");
    setCreateOpen(true);
  };
  const closeCreate = () => { setCreateOpen(false); setCreateTarget(null); setCreatePin(""); };

  const openUpdate = (d) => {
    setUpdateTarget({ id: userIdOf(d), prenom: d.prenom, nom: d.nom, email: d.email || d.user?.email, name: d.user?.name });
    setUpdatePin("");
    setUpdateOpen(true);
  };
  const closeUpdate = () => { setUpdateOpen(false); setUpdateTarget(null); setUpdatePin(""); };

  const refreshAccountByEmail = async (email) => {
    try {
      const { data } = await axios.get(`${api}/bank/accounts/lookup`, { ...auth, params: { email } });
      if (data?.user?.id) {
        setAccounts(prev => ({ ...prev, [data.user.id]: data.account || null }));
      }
    } catch {}
  };

  // Créer compte + PIN + (IBAN auto côté backend)
  const createAccount = async () => {
    if (!/^\d{4}$/.test(createPin)) return alert("Le PIN doit contenir 4 chiffres.");
    try {
      setCreating(true);
      await axios.post(
        `${api}/bank/accounts/${createTarget.id}/pin`,
        { pin: createPin, devise: createDevise }, // IBAN généré côté serveur
        auth
      );
      // re-fetch pour récupérer IBAN + carte
      await refreshAccountByEmail(createTarget.email);
      closeCreate();
      alert("Compte créé (PIN + IBAN + carte virtuelle).");
    } catch (e) {
      alert(e?.response?.data?.error || "Erreur lors de la création du compte");
    } finally {
      setCreating(false);
    }
  };

  // Modifier PIN
  const updateAccountPin = async () => {
    if (!/^\d{4}$/.test(updatePin)) return alert("Le PIN doit contenir 4 chiffres.");
    try {
      setUpdating(true);
      await axios.put(
        `${api}/bank/accounts/${updateTarget.id}/pin`,
        { pin: updatePin },
        auth
      );
      await refreshAccountByEmail(updateTarget.email);
      closeUpdate();
      alert("PIN mis à jour.");
    } catch (e) {
      alert(e?.response?.data?.error || "Erreur lors de la mise à jour du PIN");
    } finally {
      setUpdating(false);
    }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); alert("Copié"); } catch {}
  };

  if (loading) return <p>Chargement…</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div className="container py-3">
      <h4 className="mb-3">Utilisateurs validés — Création/Modification PIN &amp; Carte (IBAN auto)</h4>

      {loadingLookups && (
        <div className="alert alert-info py-2">Chargement des informations de comptes…</div>
      )}

      <div className="row g-3">
        {demandes.length === 0 ? (
          <div className="col-12">Aucune demande validée.</div>
        ) : (
          demandes.map((d) => {
            const name = holderName(d);
            const email = d.email || d.user?.email || "";
            const uId = userIdOf(d);
            const account = uId ? accounts[uId] : null;

            const ibanRaw = account?.iban || null;              // suppose que l’API renvoie iban
            const ibanFmt = formatIban(ibanRaw);
            const ibanMasked = maskIban(ibanRaw);

            return (
              <div className="col-12 col-md-6 col-xl-4" key={`card-${d.id}-${email}`}>
                <div className="card shadow-sm h-100">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <div className="avatar-circle" title={name}>
                        {initials({ prenom: d.prenom, nom: d.nom, name: d.user?.name })}
                      </div>
                      <div>
                        <div className="fw-bold">{name}</div>
                        <div className="text-muted" style={{ fontSize: 13 }}>{email}</div>
                      </div>
                    </div>

                    <div className="mt-2">
                      {account ? (
                        <VirtualCard
                          card={account.card}
                          holder={name}
                        />
                      ) : (
                        <div className="vf-card vf-card-empty">
                          <div className="vf-number">Pas encore de carte</div>
                          <div className="vf-label">Créez le compte pour générer la carte</div>
                        </div>
                      )}
                    </div>

                    <ul className="list-unstyled mt-3 mb-0 small">
                      <li>
                        <strong>Carte :</strong>{" "}
                        {account?.card?.number_masked || "—"}
                      </li>
                      <li>
                        <strong>Expiration :</strong>{" "}
                        {account?.card ? `${account.card.exp_month}/${account.card.exp_year}` : "—"}
                      </li>
                      <li>
                        <strong>PIN :</strong>{" "}
                        {account ? "défini (non affiché)" : "non défini"}
                      </li>
                      <li className="mt-1">
                        <strong>IBAN :</strong>{" "}
                        {ibanMasked ? (
                          <>
                            <span title={ibanFmt}>{formatIban(ibanMasked)}</span>{" "}
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 align-baseline"
                              onClick={() => copy(ibanRaw)}
                              disabled={!ibanRaw}
                              title="Copier l'IBAN"
                            >
                              Copier
                            </button>
                          </>
                        ) : (account ? "—" : "non généré")}
                      </li>
                    </ul>

                    <div className="d-flex gap-2 mt-auto pt-3">
                      {!account ? (
                        <button className="btn btn-primary w-100" onClick={() => openCreate(d)}>
                          Créer compte (PIN + IBAN)
                        </button>
                      ) : (
                        <button className="btn btn-outline-primary w-100" onClick={() => openUpdate(d)}>
                          Modifier PIN
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal CRÉER COMPTE */}
      {createOpen && (
        <div className="modal-backdrop-custom" onClick={closeCreate}>
          <div className="modal-card" onClick={(e)=>e.stopPropagation()}>
            <h6 className="mb-1">Créer compte + PIN (IBAN auto)</h6>
            <p className="text-muted" style={{ fontSize: 13 }}>
              {`${createTarget?.prenom ?? ""} ${createTarget?.nom ?? ""}`.trim() || createTarget?.name} — {createTarget?.email}
            </p>
            <div className="row g-2">
              <div className="col-12 col-sm-6">
                <label className="form-label">PIN (4 chiffres)</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  className="form-control"
                  placeholder="0000"
                  value={createPin}
                  onChange={(e)=>setCreatePin(e.target.value.replace(/\D/g,'').slice(0,4))}
                />
              </div>
              <div className="col-12 col-sm-6">
                <label className="form-label">Devise</label>
                <select
                  className="form-select"
                  value={createDevise}
                  onChange={(e)=>setCreateDevise(e.target.value)}
                >
                  <option value="EUR">EUR</option>
                  <option value="XOF">XOF</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-outline-secondary" onClick={closeCreate}>Annuler</button>
              <button className="btn btn-primary" onClick={createAccount} disabled={creating || !/^\d{4}$/.test(createPin)}>
                {creating ? "Création…" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal MODIFIER PIN */}
      {updateOpen && (
        <div className="modal-backdrop-custom" onClick={closeUpdate}>
          <div className="modal-card" onClick={(e)=>e.stopPropagation()}>
            <h6 className="mb-1">Modifier le PIN</h6>
            <p className="text-muted" style={{ fontSize: 13 }}>
              {`${updateTarget?.prenom ?? ""} ${updateTarget?.nom ?? ""}`.trim() || updateTarget?.name} — {updateTarget?.email}
            </p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              className="form-control"
              placeholder="Nouveau PIN (4 chiffres)"
              value={updatePin}
              onChange={(e)=>setUpdatePin(e.target.value.replace(/\D/g,'').slice(0,4))}
            />
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button className="btn btn-outline-secondary" onClick={closeUpdate}>Annuler</button>
              <button className="btn btn-primary" onClick={updateAccountPin} disabled={updating || !/^\d{4}$/.test(updatePin)}>
                {updating ? "Mise à jour…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .avatar-circle { width:56px;height:56px;border-radius:50%;background:#2563eb;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700; }
        .vf-card { background: linear-gradient(135deg, #1f2937, #111827); color: #e5e7eb; border-radius: 16px; padding: 14px 16px; position: relative; box-shadow: 0 8px 20px rgba(0,0,0,.25); min-height: 110px; }
        .vf-card-empty { display:flex; flex-direction:column; align-items:flex-start; justify-content:center; gap:6px; }
        .vf-chip { width: 34px; height: 24px; background: linear-gradient(145deg,#d1b65e,#a8892d); border-radius: 6px; opacity:.9; }
        .vf-brand { position:absolute; right:16px; top:12px; font-weight:700; letter-spacing:.6px; opacity:.9; }
        .vf-number { letter-spacing:2px; font-size:18px; margin-top:10px; }
        .vf-label { font-size:11px; opacity:.8; }
        .vf-value { font-size:13px; font-weight:600; }
        .modal-backdrop-custom { position: fixed; inset: 0; background: rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:1050; padding:1rem; }
        .modal-card { background:#fff; border-radius:12px; padding:16px; width:100%; max-width:520px; box-shadow:0 12px 36px rgba(0,0,0,.2); }
        @media (min-width:576px){ .modal-card{ padding:20px; } }
      `}</style>
    </div>
  );
}
